import { Box, Button, Flex, Heading, HStack, Tag, Text, Tooltip, useDisclosure, UnorderedList, ListItem } from '@chakra-ui/react';
import { useGetSkillsQuery, useDeleteSkillMutation, useModalitiesQuery } from '@/app/services/nucleus';
import { useState, useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { BsPlus, BsTrash, BsPencilSquare } from 'react-icons/bs';
import { FiFileText } from 'react-icons/fi';
import SkillsModal from './SkillsModal';
import EditSkillModalities from './EditSkillModalities';
import PreviewChecklistModal from './PreviewChecklistModal';
import ModalComponent from '@/features/common/ModalComponent';
import { useSearchParams } from 'react-router-dom';
import TableButton from '../common/table/TableButton';
import { usePagination } from '@/hooks/usePagination.js';
import { useServerSideSorting } from '@/hooks/useServerSideSorting.js';
import SSDataTable from '@/features/common/table/SSDataTable.jsx';
import { isRealValue } from '@/utils/helpers.js';
import SearchInput from '@/features/common/SearchInput.jsx';
import { useDebouncedSearchSync } from '@/hooks/useDebouncedSearchSync.js';
import AlertComponent from '../common/AlertComponent';
import TruncatedArrayComponent from '@/features/common/TruncatedArrayComponent';
import { Select } from 'chakra-react-select';
import { chakraStyles } from '../common/select/styles';

const SkillsPage = () => {
  const columnHelper = createColumnHelper();

  const [searchText, setSearchText] = useState('');
  const [selectedModality, setSelectedModality] = useState(null);
  const [pageCount, setPageCount] = useState();
  const [urlParamsParsed, setUrlParamsParsed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [pendingDeleteSkill, setPendingDeleteSkill] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);

  const {
    limit,
    onPaginationChange,
    pagination: paginationState,
    offset,
  } = usePagination({
    onPageChange: (pageState) => {
      setSearchParams((params) => {
        params.set('page', pageState.pageIndex);
        params.set('limit', pageState.pageSize);
        return params;
      });
    },
  });

  const { sorting, onSortingChange, sortByField } = useServerSideSorting([{ id: 'description', desc: false }]);

  const { data: modalities } = useModalitiesQuery();

  const modalityOptions = useMemo(() => {
    return (modalities?.items || []).map((m) => ({ value: m.id, label: m.short_name }));
  }, [modalities?.items]);

  const {
    data: skills,
    isLoading,
    isError,
    error: skillsError,
  } = useGetSkillsQuery(
    {
      search: searchText,
      sortBy: sortByField,
      limit,
      offset,
      modalityId: selectedModality?.value,
    },
    { skip: !urlParamsParsed },
  );

  useDebouncedSearchSync({ searchText, onPaginationChange, limit, setSearchParams, delay: 200 });

  useEffect(() => {
    if (isRealValue(skills?.total)) {
      setPageCount(Math.ceil(skills?.total / limit));
    }
  }, [skills?.total, limit]);

  useEffect(() => {
    const pageState = { ...paginationState };
    for (let param of searchParams) {
      if (Array.isArray(param) && param.at(0) === 'search') {
        setSearchText(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'page') {
        pageState.pageIndex = Number(param.at(1));
      }
      if (Array.isArray(param) && param.at(0) === 'limit') {
        pageState.pageSize = Number(param.at(1));
      }
    }
    onPaginationChange(pageState);
    setUrlParamsParsed(true);
  }, []);

  const [deleteSkill] = useDeleteSkillMutation();

  const handleDeleteSkill = (id, description) => {
    setPendingDeleteSkill({ id, description });
    onDeleteOpen();
  };

  const handleConfirmDelete = () => {
    if (!pendingDeleteSkill?.id) return;
    deleteSkill(pendingDeleteSkill.id);
    onDeleteClose();
  };

  const handleSearchTextChange = (event) => {
    if (event.target.value === '') {
      handleClearSearchText();
    } else {
      setSearchText(event.target.value);
    }
  };

  const handleClearSearchText = () => {
    setSearchText('');
    searchParams.delete('search');
    setSearchParams(searchParams);
  };

  const handleNewSkill = () => {
    onOpen();
  };

  const handleEditModalities = (skill) => {
    setSelectedSkill(skill);
    onEditOpen();
  };

  const handleModalityChange = (option) => {
    setSelectedModality(option);
    onPaginationChange({ pageSize: limit, pageIndex: 0 });
  };

  const skillColumns = [
    columnHelper.accessor('description', { header: 'Description', size: 400 }),
    columnHelper.accessor('category', { header: 'Category', size: 400 }),
    columnHelper.accessor('modalities', {
      header: 'Modalities',
      disableSortBy: true,
      cell: ({ getValue }) => {
        const items = getValue() || [];
        const truncatedItems = items.slice(0, 8);
        const truncatedString = truncatedItems.map((m) => m.short_name).join(', ');
        const row = { modalitiesPopover: items, modalitiesTruncated: truncatedString, modalitiesTruncatedList: truncatedItems };
        return (
          <TruncatedArrayComponent
            title="Modalities"
            fieldName="modalities"
            row={row}
            renderTruncated={(list) =>
              list.map((m) => (
                <Tag key={m.id} size="sm" colorScheme="purple" borderRadius="full">
                  {m.short_name}
                </Tag>
              ))
            }
            renderItems={(list) => (
              <UnorderedList>
                {list.map((m) => (
                  <ListItem key={m.id}>{m.short_name}</ListItem>
                ))}
              </UnorderedList>
            )}
          />
        );
      },
    }),
    columnHelper.accessor('', {
      header: 'Action',
      disableSortBy: true,
      cell: (info) => {
        const skill = info.row.original;
        return (
          <HStack>
            <Tooltip hasArrow placement="top" label="Edit Modalities">
              <Box p="0" as="span">
                <TableButton aria-label="edit-modalities" icon={<BsPencilSquare />} onClick={() => handleEditModalities(skill)} />
              </Box>
            </Tooltip>
            <Tooltip hasArrow placement="top" label="Delete Skill">
              <Box p="0" as="span">
                <TableButton aria-label="delete" icon={<BsTrash />} onClick={() => handleDeleteSkill(skill?.id, skill?.description)} />
              </Box>
            </Tooltip>
          </HStack>
        );
      },
    }),
  ];

  return (
    <Box data-test-id="pact-skills-page">
      <Heading as="h4" size="md" fontWeight="bold" color="brand.900">
        Skills
      </Heading>
      <Flex gap={3} rowGap={3} flexWrap="wrap" alignItems="center" width="100%" py={5}>
        <Flex gap={3} flexWrap="nowrap" alignItems="center" flex="1 1 420px" minW={0}>
          <Box flex="1 1 0" minW="140px">
            <SearchInput
              isDisabled={false}
              searchText={searchText}
              dataType="text"
              onChange={handleSearchTextChange}
              onClear={handleClearSearchText}
            />
          </Box>
          <Box flex="1 1 0" minW="160px">
            <Select
              chakraStyles={{
                ...chakraStyles,
                placeholder: (provided) => ({
                  ...provided,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }),
              }}
              focusBorderColor="brand.700"
              options={modalityOptions}
              value={selectedModality}
              onChange={handleModalityChange}
              isClearable
              isSearchable
              placeholder="Filter by Modality"
            />
          </Box>
        </Flex>
        <Flex gap={3} rowGap={3} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
          <Button leftIcon={<FiFileText />} onClick={onPreviewOpen} variant="outline" type="button" flexShrink={0}>
            Preview checklist document
          </Button>
          <Button leftIcon={<BsPlus />} onClick={handleNewSkill} variant="purple" type="button" flexShrink={0}>
            New
          </Button>
        </Flex>
      </Flex>
      {isError ? (
        <AlertComponent status="warning" title="Skills Error" description={skillsError?.data?.detail ?? 'Error fetching skills'} />
      ) : (
        <SSDataTable
          loading={isLoading}
          data={skills?.items}
          total={skills?.total ?? 0}
          columns={skillColumns}
          pagination={paginationState}
          pageCount={pageCount}
          onPaginationChange={onPaginationChange}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      )}
      <SkillsModal isOpen={isOpen} onClose={onClose} onSave={onClose} modalityOptions={modalityOptions} />
      <EditSkillModalities isOpen={isEditOpen} onClose={onEditClose} skill={selectedSkill} modalityOptions={modalityOptions} />
      <PreviewChecklistModal isOpen={isPreviewOpen} onClose={onPreviewClose} modalityOptions={modalityOptions} />
      <ModalComponent
        size="sm"
        title="Delete Skill"
        primaryText="Yes"
        secondaryText="No"
        handleConfirm={handleConfirmDelete}
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        isError={false}
      >
        <Box>
          <Text>
            Are you sure you want to delete <strong>{pendingDeleteSkill?.description}</strong>? This permanently removes it from the catalog.
          </Text>
        </Box>
      </ModalComponent>
    </Box>
  );
};

export default SkillsPage;
